import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import {
    ALWAYS_APPROVE_POLICY_TYPE_ID,
    ALWAYS_DENY_POLICY_ID,
    ALWAYS_OBLIGATE_POLICY_ID,
    TRUST_SCORE_POLICY_ID
} from "@constants/verification-service-details-constants";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { PolicyComponentTypes } from "@enums/policy-component-types";
import { PolicyTypes } from "@enums/policy-types";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationPolicy,
    FormattedRpServiceConfigurationPolicyConfig,
    FormattedRpServiceConfigurationServiceOffering,
    FormattedRpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import {
    DeployedPolicyComponent,
    SimpleDeployedPolicyComponentCondition
} from "@idw-types/data/reference-data/generic";
import { AxnRpService } from "@idw-types/data/axn-rp-service";

import { FormattedAxnRpService } from "@idw-types/data/axn-rp-service/axn-rp-service";
import { AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { sortStrings } from "@utils/string-util";
import { VerificationTransactionDetailsPolicyDecisionConditionResult } from "@idw-types/data/verification-transaction-details";
import { POLICY_TYPE_DEFAULT_OPTION, POLICY_TYPE_OPTIONS } from "@constants/policy-type-options";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS } from "@constants/policy-config-decision-attribute-field-result-options";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import { AXNUIContentPolicyDecisionRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-content";

export const getPolicyIdFromPolicyType = (policyType: PolicyTypes) => {
    switch (policyType) {
        case PolicyTypes.ALWAYS_APPROVE:
            return ALWAYS_APPROVE_POLICY_TYPE_ID;
        case PolicyTypes.ALWAYS_DENY:
            return ALWAYS_DENY_POLICY_ID;
        case PolicyTypes.ALWAYS_OBLIGATE:
            return ALWAYS_OBLIGATE_POLICY_ID;
        case PolicyTypes.TRUST_SCORE:
            return TRUST_SCORE_POLICY_ID;
        default:
            return null;
    }
};

export const getPolicyTypeById = (usePolicyManager: boolean, policyConfig: FormattedRpServiceConfigurationPolicyConfig, policies: Array<FormattedRpServiceConfigurationPolicy>) => {
    let selectedPolicyType: PolicyTypes = null;

    // if not using policy manager, policy type is none
    if (!usePolicyManager) {
        selectedPolicyType = PolicyTypes.NONE;
    }
    // if using policy manager and policy config is active, policy type is decision engine
    else if (policyConfig?.status === EntityStatusTypes.ACTIVE) {
        selectedPolicyType = PolicyTypes.DECISION_ENGINE;
    }
    // if we have policies, we can derive the policy type from them
    else if ((policies || []).length > 0) {
        const policy = policies[0];
        // first check that the policy is active
        if (policy.status === EntityStatusTypes.ACTIVE) {
            // then match up policyID to determine policy type
            if (policy.policyID === ALWAYS_APPROVE_POLICY_TYPE_ID) {
                selectedPolicyType = PolicyTypes.ALWAYS_APPROVE;
            } else if (policy.policyID === ALWAYS_DENY_POLICY_ID) {
                selectedPolicyType = PolicyTypes.ALWAYS_DENY;
            } else if (policy.policyID === ALWAYS_OBLIGATE_POLICY_ID) {
                selectedPolicyType = PolicyTypes.ALWAYS_OBLIGATE;
            } else if (policy.policyID === TRUST_SCORE_POLICY_ID) {
                selectedPolicyType = PolicyTypes.TRUST_SCORE;
            }
        }
    }

    return selectedPolicyType;
}

export const getNewPoliciesFromPolicyType = (
    rpServiceConfiguration: FormattedRpServiceConfiguration | Partial<FormattedRpServiceConfiguration>,
    policyType: PolicyTypes,
    oldPolicies: Array<FormattedRpServiceConfigurationPolicy>
): Array<FormattedRpServiceConfigurationPolicy> => {
    const policyId = getPolicyIdFromPolicyType(policyType);
    const existingPolicies = oldPolicies || [];

    // if there is an existing policy - modify it
    if (existingPolicies.length > 0) {
        const oldPolicy = existingPolicies[0];
        // if always obligate, set to active and add default property
        if (policyType === PolicyTypes.ALWAYS_OBLIGATE) {
            return [{
                ...oldPolicy,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                properties: [{
                    name: "obligation.apikey",
                    selectedPolicyID: "",
                    value: "",
                    id: undefined
                }]
            }];
        }
        // if trust score, set to active and add default properties
        else if (policyType === PolicyTypes.TRUST_SCORE) {
            return [{
                ...oldPolicy,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                stepUpEnabled: false,
                properties: [
                    {
                        name: "idwRiskScore.pass.minValue",
                        selectedPolicyID: oldPolicy.id,
                        value: 70,
                        id: undefined
                    },
                    {
                        name: "idwRiskScore.stepUp.minValue",
                        selectedPolicyID: oldPolicy.id,
                        value: null,
                        id: undefined
                    },
                    {
                        name: "stepUp.apiKey",
                        selectedPolicyID: oldPolicy.id,
                        value: "",
                        id: undefined
                    }
                ]
            }];
        }
        // if none - set to inactive
        else if (policyType === PolicyTypes.NONE) {
            return [{
                ...oldPolicy,
                status: EntityStatusTypes.INACTIVE
            }];
        }
        // if decision engine - set to active, but create a new policy ID **todo - double check this logic
        else if (policyType === PolicyTypes.DECISION_ENGINE) {
            return [{
                ...oldPolicy,
                policyID: null,
                status: EntityStatusTypes.ACTIVE
            }];
        }
        // if always approve or deny, clear out properties and set to active
        else if ([PolicyTypes.ALWAYS_APPROVE, PolicyTypes.ALWAYS_DENY].includes(policyType)) {
            return [{
                ...oldPolicy,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                properties: []
            }];
        } else {
            //should never hit this
            return [oldPolicy];
        }
    }
    // if there is not an existing policy, create one (or don't if policyType = NONE)
    else {
        // if always obligate, set to active and add default property
        if (policyType === PolicyTypes.ALWAYS_OBLIGATE) {
            return [{
                apiKey: rpServiceConfiguration.apiKey,
                id: null, //todo - check this
                rpAXNServiceID: rpServiceConfiguration.id,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                properties: [{
                    name: "obligation.apikey",
                    selectedPolicyID: "", //todo - check this
                    value: "",
                    id: undefined
                }],
                stepUpEnabled: false
            }];
        }
        // if trust score, set to active and add default properties
        else if (policyType === PolicyTypes.TRUST_SCORE) {
            return [{
                apiKey: rpServiceConfiguration.apiKey,
                id: null, //todo - check this
                rpAXNServiceID: rpServiceConfiguration.id,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                properties: [
                    {
                        name: "idwRiskScore.pass.minValue",
                        selectedPolicyID: "", //todo - check this
                        value: 70,
                        id: undefined
                    },
                    {
                        name: "idwRiskScore.stepUp.minValue",
                        selectedPolicyID: "", //todo - check this
                        value: null,
                        id: undefined
                    },
                    {
                        name: "stepUp.apiKey",
                        selectedPolicyID: "", //todo - check this
                        value: "",
                        id: undefined
                    }
                ],
                stepUpEnabled: false
            }];
        } else if ([PolicyTypes.ALWAYS_APPROVE, PolicyTypes.ALWAYS_DENY, PolicyTypes.DECISION_ENGINE].includes(policyType)) {
            // if always approve or always deny or decision engine, create generic policy
            return [{
                apiKey: rpServiceConfiguration.apiKey,
                id: null, //todo - check this
                rpAXNServiceID: rpServiceConfiguration.id,
                policyID: policyId,
                status: EntityStatusTypes.ACTIVE,
                properties: [],
                stepUpEnabled: false
            }];
        } else if (policyType === PolicyTypes.NONE) {
            // if none and no policies conifgured, return empty array
            return [];
        }
    }

    return [];
};

export const getNewPolicyConfigFromPolicyType = (
    rpServiceConfiguration: FormattedRpServiceConfiguration | Partial<FormattedRpServiceConfiguration>,
    policyType: PolicyTypes,
    oldPolicyConfig: FormattedRpServiceConfigurationPolicyConfig
): FormattedRpServiceConfigurationPolicyConfig => {
    // if not decision engine, but there is an existing config, inactive it
    if (policyType !== PolicyTypes.DECISION_ENGINE && oldPolicyConfig) {
        return {
            ...oldPolicyConfig,
            status: EntityStatusTypes.INACTIVE
        };
    }
    // if decision engine and there is an existing config, activate it
    else if (policyType === PolicyTypes.DECISION_ENGINE && oldPolicyConfig) {
        return {
            ...oldPolicyConfig,
            status: EntityStatusTypes.ACTIVE
        };
    }
    // if decision engine (and no config), create a default config
    else if (policyType === PolicyTypes.DECISION_ENGINE) {
        return {
            decisions: [],
            defaultAction: PolicyDecisionActionTypes.DENY,
            defaultActionRetries: "1",
            defaultActionStepUpApiKey: null,
            description: null,
            id: null,
            name: rpServiceConfiguration.serviceName,
            policyRetries: "1",
            rpAXNServiceID: rpServiceConfiguration.id,
            status: EntityStatusTypes.ACTIVE
        };
    } else {
        //every other case policyConfig should already be null, so return null
        return null;
    }
};

const checkIfRequirementsForComponentConditionArePresent = (
    componentCondition: SimpleDeployedPolicyComponentCondition,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>,
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>,
    attributes: Array<FormattedRpServiceConfigurationAttribute & { fieldId: string }>
) => {
    // if the condition relies on another component - find it and check that component (recursion)
    if (componentCondition.componentID) {
        const component = deployedPolicyComponents.find(dpc => dpc.id === componentCondition.componentID);
        if (component) {
            const componentRequirementsPresent: Array<boolean> = [];
            const componentConditions = simpleDeployedPolicyComponentConditions.filter(condition => condition.policyComponentID === component.id);
            componentConditions.forEach(condition => {
                const conditionRequirementsPresent = checkIfRequirementsForComponentConditionArePresent(
                    condition,
                    deployedPolicyComponents,
                    simpleDeployedPolicyComponentConditions,
                    assertions,
                    attributes
                );
                componentRequirementsPresent.push(conditionRequirementsPresent);
            });

            // every assertion must be present
            if (component.type === PolicyComponentTypes.AND) {
                return componentRequirementsPresent.every(value => value);
            }
            // at least one assertion must be present
            else if (component.type === PolicyComponentTypes.OR) {
                return componentRequirementsPresent.length > 0 ? componentRequirementsPresent.some(value => value) : true;
            }
        }
    }
    // if the condition has an assertion id, check that the assertion is present in the assertions list
    else if (componentCondition.assertionID) {
        return !!assertions.find(assertion => assertion.assertionId === componentCondition.assertionID);
    }
    //if the condition has an attribute id, check that the attribute is present in the attributes list
    else if (componentCondition.attributeFieldID) {
        return !!attributes.find(attribute => attribute.fieldId === componentCondition.attributeFieldID);
    }

    return false;
};

const checkIfRequirementsForComponentArePresent = (
    component: DeployedPolicyComponent,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>,
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>,
    attributes: Array<FormattedRpServiceConfigurationAttribute & { fieldId: string }>
) => {
    const componentConditions = simpleDeployedPolicyComponentConditions.filter(condition => condition.policyComponentID === component.id);

    const componentRequirementsPresent: Array<boolean> = [];
    componentConditions.forEach(componentCondition => {
        const conditionRequirementsPresent = checkIfRequirementsForComponentConditionArePresent(
            componentCondition,
            deployedPolicyComponents,
            simpleDeployedPolicyComponentConditions,
            assertions,
            attributes
        );
        componentRequirementsPresent.push(conditionRequirementsPresent);
    });

    if (component.type === PolicyComponentTypes.AND) {
        // check that every sub component has the correct assertions present
        return componentRequirementsPresent.every(value => value);
    } else if (component.type === PolicyComponentTypes.OR) {
        // array.some return false if length = 0, so we need to override that
        return componentRequirementsPresent.length > 0 ? componentRequirementsPresent.some(value => value) : true;
    }

    return false;
};


export const getComponentsToDisplay = (
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>,
    assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion>,
    attributes: Array<FormattedRpServiceConfigurationAttribute & { fieldId: string }>
) => {
    // for each component - check that all of the required assertions are present
    const filteredComponents = deployedPolicyComponents.filter(component => {
        return checkIfRequirementsForComponentArePresent(
            component,
            deployedPolicyComponents,
            simpleDeployedPolicyComponentConditions,
            assertions,
            attributes
        );
    });

    return filteredComponents;
};

export const injectLabel = (rpService: AxnRpService): FormattedAxnRpService => {
    const label = `${rpService.serviceName} (${rpService.apiKey})`;
    return { ...rpService, label };
}

export const getObligationStepUpToList = (rpServiceID: string, workflowID: string, rpServices: Array<AxnRpService>, simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>) => {
    if (workflowID !== null) {
        const rpServiceWorkflows = simpleRPServiceWorkflows.filter(serviceWorkflow => serviceWorkflow.workflowID === workflowID);
        const rpServiceWorkflowRpServiceIds = rpServiceWorkflows.map(rpService => rpService.rpAXNServiceID);
        return rpServices.filter(service => service.status === EntityStatusTypes.ACTIVE && rpServiceWorkflowRpServiceIds.includes(service.id)).map(injectLabel);
    } else {
        return getServicesNotBelongToWorkflows(rpServiceID, rpServices, simpleRPServiceWorkflows).map(injectLabel);
    }
}

export const getServicesNotBelongToWorkflows = (rpServiceId: string, rpServices: Array<AxnRpService>, simpleAXNAdminRPServiceWorkflows: Array<SimpleRPServiceWorkflow>) => {
    const apiKeys = simpleAXNAdminRPServiceWorkflows.map(serviceWorkflow => serviceWorkflow.apiKey);
    return rpServices.filter(rpService => {
        return rpService.status === EntityStatusTypes.ACTIVE && !apiKeys.includes(rpService.apiKey) && rpServiceId !== rpService.id;
    });
}

export const isObligationApiKeyCorrect = (obligationApiKey: string, obligationRpServices: Array<FormattedAxnRpService>) => {
    return obligationRpServices.some(service => service.apiKey === obligationApiKey);

}

export const getAxnRpServiceOptions = (obligationStepUpList: Array<FormattedAxnRpService>, obligationValue: string, axnRpServices: Array<AxnRpService>) => {
    let isApiKeyPresent = false;
    const options = obligationStepUpList
        .map(service => {
            if (obligationValue && service.apiKey === obligationValue) {
                isApiKeyPresent = true;
            }

            return ({ label: service.label, value: service.apiKey });
        });

    if (obligationValue && !isApiKeyPresent) {
        const service = axnRpServices.find(rpService => rpService.apiKey === obligationValue);
        if (service) {
            options.push({ label: `${service.serviceName} (${service.apiKey})`, value: service.apiKey })
        }
    }

    return options.sort((a, b) => sortStrings(a.label, b.label));
}

export const getPolicyDecisionResult = (
    component: DeployedPolicyComponent,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>,
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>
): Array<VerificationTransactionDetailsPolicyDecisionConditionResult> => {

    const policyDecisionResult: VerificationTransactionDetailsPolicyDecisionConditionResult = {
        name: component.name,
        description: component.description,
        condition: component.type === "AND" ? "Must Pass All" : "Must Pass One or More",
        result: "pass",
        details: checkPolicyComponent(
            component.id,
            deployedPolicyComponents,
            simpleDeployedPolicyComponentConditions,
            serviceOfferings,
            axnAttributeTypeFields
        )
    };

    return [policyDecisionResult];
}

export const getPolicyConditionResultDescription = (name: string, result: Array<string>) => {
    return `${name} = ${result.join(" or")}`;
}

export const checkPolicyComponent = (
    componentID: string,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    simpleDeployedPolicyComponentConditions: Array<SimpleDeployedPolicyComponentCondition>,
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>
) => {
    const policyConditions = simpleDeployedPolicyComponentConditions.filter(condition => condition.policyComponentID === componentID);

    const res = policyConditions.reduce((policyDecisionResultDetails, condition) => {
        if (condition.componentID) {
            const component = deployedPolicyComponents.find(component => component.id === condition.componentID);

            if (component) {
                const policyDecisionResult: VerificationTransactionDetailsPolicyDecisionConditionResult = {
                    name: component.name,
                    description: component.description,
                    condition: component.type === "AND" ? "Must Pass All" : "Must Pass One or More",
                    result: condition.result || 'pass',
                    details: checkPolicyComponent(
                        condition.componentID,
                        deployedPolicyComponents,
                        simpleDeployedPolicyComponentConditions,
                        serviceOfferings,
                        axnAttributeTypeFields
                    )
                };

                policyDecisionResultDetails.push(policyDecisionResult);
            }
        } else if (condition.serviceOfferingID && condition.assertionID) {
            const serviceOffering = serviceOfferings.find(so => so.serviceOfferingID === condition.serviceOfferingID);
            if (serviceOffering) {
                const assertion = serviceOffering.assertions.find(assertion => assertion.assertionId === condition.assertionID);

                let name = `${serviceOffering.name}`;
                if (assertion) {
                    // in this case the serviceOffering ID and assertion ID are not null
                    name += ` ${assertion.shortName}`;

                } else {
                    name += ` UNKNOWN ASSERTION`
                }

                policyDecisionResultDetails.push({
                    name,
                    description: getPolicyConditionResultDescription(name, condition.values.split(',')),
                    condition: null,
                    result: condition.result || 'pass',
                    details: null
                });
            }
        } else if (condition.attributeFieldID) {
            //in this case the attribute field ID is not null
            const attributeTypeField = axnAttributeTypeFields.find(atf => atf.id === condition.attributeFieldID);

            if (attributeTypeField) {
                let name = `${attributeTypeField.name}`;

                const policyConditionOperator = POLICY_CONFIG_DECISION_ATTRIBUTE_FIELD_RESULT_OPTIONS.find(pco => pco.value === condition.policyConditionOperator);
                if (policyConditionOperator) {
                    name += ` ${policyConditionOperator.label} ${condition.values}`;
                } else {
                    name += ` ${condition.policyConditionOperator} ${condition.values}`;
                }

                policyDecisionResultDetails.push({
                    name,
                    description: getPolicyConditionResultDescription(attributeTypeField.name, condition.values.split(',')),
                    condition: null,
                    result: condition.result || 'pass',
                    details: null
                });
            }
        }

        return policyDecisionResultDetails;
    }, [] as Array<VerificationTransactionDetailsPolicyDecisionConditionResult>);

    return res;
}

export const getPolicyOptions = (selectedPolicyTypeOption: SelectOption, initialPolicyTypeOption: SelectOption) => {
    const decisionEngineOption = { label: "Decision Engine", value: PolicyTypes.DECISION_ENGINE };

    if (
        selectedPolicyTypeOption.value === PolicyTypes.DECISION_ENGINE &&
        initialPolicyTypeOption.value !== PolicyTypes.DECISION_ENGINE
    ) {
        return [initialPolicyTypeOption, decisionEngineOption];
    } else if (
        selectedPolicyTypeOption.value !== PolicyTypes.DECISION_ENGINE &&
        initialPolicyTypeOption.value !== PolicyTypes.NONE
    ) {
        return [selectedPolicyTypeOption, decisionEngineOption];
    } else if (
        selectedPolicyTypeOption.value === PolicyTypes.DECISION_ENGINE &&
        initialPolicyTypeOption.value === PolicyTypes.DECISION_ENGINE
    ) {
        return [decisionEngineOption];
    }

    return [POLICY_TYPE_DEFAULT_OPTION, decisionEngineOption];
}

export const getPolicyTypeOption = (policyType: PolicyTypes) => {
    const selectedOption = POLICY_TYPE_OPTIONS.find(pto => pto.value === policyType);
    if (!selectedOption) {
        return POLICY_TYPE_DEFAULT_OPTION;
    } else {
        return selectedOption;
    }
}


export const getDecisionEngineRetryMessageToCopy = (decisionToBeCopiedUuid: string, decisionToBeCopiedId: string, policyDecisionRetryMessage: AXNUIContentPolicyDecisionRetryMessage): string => {    
    if (decisionToBeCopiedId && policyDecisionRetryMessage[decisionToBeCopiedId]?.retryMessage) {
        return policyDecisionRetryMessage[decisionToBeCopiedId].retryMessage
    } else if (policyDecisionRetryMessage[decisionToBeCopiedUuid]?.retryMessage) {
        return policyDecisionRetryMessage[decisionToBeCopiedUuid].retryMessage;
    } else {
        return policyDecisionRetryMessage.default.retryMessage;
    }
}